import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MasterdataService } from '../../../../services/master-data/masterdata.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SidebarService } from '../../../services/sidebar.service';
import { TooltipModule } from 'primeng/tooltip';
import { HostListener, ElementRef } from '@angular/core';
import {
  canNavigateNotification,
  getNotificationModuleIcon,
  getNotificationModuleName,
  isDeleteNotification,
  navigateNotificationToMap,
} from './notification-navigation';

@Component({
  selector: 'app-notification-dropdown',
  templateUrl: './notification-dropdown.component.html',
  styleUrl: './notification-dropdown.component.css',
  imports: [CommonModule, RouterModule, TooltipModule, ConfirmDialogModule,],
  providers: [MessageService],
})
export class NotificationDropdownComponent implements OnInit {
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  NotificationList: any;
  unreadCountFromApi: number | null = null;
  isOpen = false;
  notifying = true;
  markingAllRead = false;

  constructor(private masterdataService: MasterdataService, private sidebarService: SidebarService, private router: Router,
    private elementRef: ElementRef
  ) {
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
    this.notifying = false;
  }

  closeDropdown() {
    this.isOpen = false;
  }

  ngOnInit() {
    this.getNotify();

    this.sidebarService.updateNotify.subscribe((notify: boolean) => {
      if (notify) {
        this.getNotify();
      }
    })
  }

  getNotify() {
    this.masterdataService.getNotificationList().subscribe({
      next: (res: any) => {
        this.NotificationList = res.data;
        this.unreadCountFromApi = typeof res?.unread_count === 'number' ? res.unread_count : null;
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', life: 4000, detail: err.message || 'An Error Occured' });
      }
    })
  }

  isDeleteNotification(item: any): boolean {
    return isDeleteNotification(item);
  }
  itemNotificationClicked(event: any, item: any, index: any) {
    const shouldNavigate = !this.isDeleteNotification(item);
    if (!item.is_read) {
      const payload = { is_read: true };
      this.masterdataService.updateNotification(payload, item.id).subscribe({
        next: (res: any) => {
          this.NotificationList[index] = res;
          this.decrementUnread();
          if (shouldNavigate) {
            this.isOpen = false;
            this.navigateToMap(item);
          }
        },
        error: (err) => {
          this.messageService.add({ severity: 'error', summary: 'Error', life: 4000, detail: err.message || 'An Error Occured' });
        }
      });
    } else if (shouldNavigate) {
      // already read — just navigate
      this.isOpen = false;
      this.navigateToMap(item);
    }
  }

  navigateToMap(item: any): void {
    navigateNotificationToMap(this.router, item);
  }

  getModuleName(moduleId: number | string): string {
    return getNotificationModuleName(moduleId);
  }
  getModuleIcon(moduleId: number | string): string {
    return getNotificationModuleIcon(moduleId);
  }
  canNavigate(item: any): boolean {
    return canNavigateNotification(item);
  }

  contentFormatter(content: any, type: any): any {
    switch (type) {
      case 'json':
        if (!content) return {};
        return JSON.parse(content);
      case 'edi':
        return this.formatEdi(content);
      default:
        return content;
    }
  }

  formatEdi(content: string): string {
    content = content.replaceAll(/\r?\n|\r/g, '').trim();  // remove extra newlines and trim
    return content
      .split("'")
      .filter(segment => segment.trim().length)
      .map(segment => segment + "'")
      .join("\n");
  }

  get unreadCount(): number {
    if (this.unreadCountFromApi !== null) return this.unreadCountFromApi;
    if (!this.NotificationList?.length) return 0;
    return this.NotificationList.filter((item: any) => !item.is_read).length;
  }

  private decrementUnread(): void {
    if (this.unreadCountFromApi !== null) {
      this.unreadCountFromApi = Math.max(0, this.unreadCountFromApi - 1);
    }
  }

  markAllRead(): void {
    if (this.unreadCount === 0 || this.markingAllRead) return;
    this.markingAllRead = true;
    this.masterdataService.markAllNotificationsRead().subscribe({
      next: () => {
        if (this.NotificationList?.length) {
          this.NotificationList = this.NotificationList.map((n: any) => ({ ...n, is_read: true }));
        }
        this.unreadCountFromApi = 0;
        this.markingAllRead = false;
        this.sidebarService.updateNotify.next(true);
      },
      error: (err: any) => {
        this.markingAllRead = false;
        this.messageService.add({ severity: 'error', summary: 'Error', life: 4000, detail: err?.message || 'Failed to mark all as read' });
      }
    });
  }

  showAll(): void {
    this.isOpen = false;
    this.router.navigate(['/notifications']);
  }

  deleteNotification(item: any, index: number, event: Event): void {
    event.stopPropagation();
    this.confirmationService.confirm({
      key: 'notification-bell',
      message: 'Delete this notification? This cannot be undone.',
      header: 'Delete notification',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true
      },
      acceptLabel: 'Delete',
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger'
      },
      accept: () => this.confirmDeleteNotification(item, index),
    });
  }

  private confirmDeleteNotification(item: any, index: number): void {
    const wasUnread = !item?.is_read;
    this.masterdataService.deleteNotificationRow(item.id).subscribe({
      next: () => {
        this.NotificationList.splice(index, 1);
        if (wasUnread) this.decrementUnread();
        this.sidebarService.updateNotify.next(true);
      },
      error: (err: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', life: 4000, detail: err?.message || 'Failed to delete' });
      }
    });
  }

  deleteAllNotifications(): void {
    if (!this.NotificationList?.length) return;
    const count = this.NotificationList.length;
    this.confirmationService.confirm({
      key: 'notification-bell',
      message: `Delete all ${count} notification${count === 1 ? '' : 's'}? This cannot be undone.`,
      header: 'Delete all notifications',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true
      },
      acceptLabel: 'Delete all',
      acceptButtonProps: {
        label: 'Delete all',
        severity: 'danger'
      },
      accept: () => this.confirmDeleteAllNotifications(),
    });
  }

  private confirmDeleteAllNotifications(): void {
    this.masterdataService.deleteNotificationBulk().subscribe({
      next: () => {
        this.NotificationList = [];
        this.unreadCountFromApi = 0;
        this.sidebarService.updateNotify.next(true);
      },
      error: (err: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', life: 4000, detail: err?.message || 'Failed to delete all' });
      }
    });
  }

  getTimeAgo(dateStr: string): string {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    return Math.floor(diff / 86400) + 'd ago';
  }

  formatNotificationDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day}-${month}-${year} | ${hours}:${minutes} ${ampm}`;
  }

}